/**
 * @file useBuilderMainAction.ts
 * @description Главное действие конструктора (phase19.16 Р1-6а; Р3а — SB105,
 * SB126; uiux-r3 §3.1 «Главное действие»; правила — `builderMainAction`):
 * окно публикации, «Опубликовать страницу» без окна или неактивная кнопка с
 * подписью. После публикации одной страницы — тост «Страница опубликована.
 * [Вернуть как было]» на 8 секунд: возврат прежней версии с `keepDraft`
 * (черновик с правками остаётся, SB126).
 */

import { sitePageAddress } from "@chaiberi/shared";
import { useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { hasDraftChanges, useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { builderMainAction, type MainAction } from "../../../lib/site-builder/publish-rules";
import { draftChangedText, pagePublishErrorText, parseDraftChanged } from "../../../lib/site-builder/page-publish-errors";
import { useOpenSite } from "../../../hooks/site-access/useOpenSite";
import { slugChangeOf } from "../../../lib/site-builder/page-publish-items";
import { notifyRedirectChange } from "../../../lib/site-seo/redirect-change";

/** Сколько держать тост «Вернуть как было» (uiux-r3 §3.1). */
export const REVERT_TOAST_MS = 8000;

export function useBuilderMainAction(): { action: MainAction; busy: boolean; run: () => void } {
  const view = useSiteView();
  const b = useBuilder();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  // «Открыть» после первой публикации: при закрытом сайте — через пропуск сотрудника (SB138).
  const openSite = useOpenSite(view.siteUrl);
  const { publishing } = b;
  const changes = b.selection.screen ? null : publishing.changes;
  const action = builderMainAction({
    viewChanged: hasDraftChanges(view.changes),
    viewBlockedByRights: (view.changes?.blockedBy.length ?? 0) > 0,
    page: changes
      ? { hasChanges: changes.hasChanges, isPublished: changes.isPublished, hasLive: changes.live !== null, hasErrors: changes.checks.length > 0, slugChanged: slugChangeOf(changes) !== null }
      : null,
    otherPagesChanged: publishing.others.length > 0,
    canPublishContent: b.canPublishPages,
  });

  async function revert(pageId: string, version: number) {
    try {
      // Привязка к опубликованной странице: человек мог уже открыть другую.
      await publishing.revertTo(pageId, version);
      notify.success("На сайте снова прежняя версия. Ваши правки остались в черновике.");
    } catch (err) {
      notify.error(pagePublishErrorText(err, "Не удалось вернуть прежнюю версию — на сайте новая."));
    }
  }

  async function publishPage() {
    setBusy(true);
    try {
      const { result, previousLive } = await publishing.publish();
      const published = result.pages[0];
      const isSlot = changes?.kind === "SLOT";
      const done = isSlot ? "Блоки места опубликованы." : "Страница опубликована.";
      // Р3в: снятые переадресации (адрес снова занят этой страницей) — тостом.
      notifyRedirectChange(published?.redirect, notify);
      if (published && previousLive !== null) {
        notify.success(done, { duration: REVERT_TOAST_MS, action: { label: "Вернуть как было", onClick: () => void revert(published.pageId, previousLive) } });
      } else {
        // Первая публикация — возвращать некуда; у страницы — «Открыть» её адрес.
        const path = published?.liveSlug ? sitePageAddress(published.liveSlug) : null;
        notify.success(done, { duration: REVERT_TOAST_MS, action: openSite && path && !isSlot ? { label: "Открыть", onClick: () => openSite(path) } : undefined });
      }
    } catch (err) {
      const changed = parseDraftChanged(err);
      if (changed) {
        // uiux-r3 §3.1: «… изменил Иван (14:40). Посмотрите правки и опубликуйте снова. [Обновить]» —
        // «Обновить» перечитывает черновик, список и канву (QA Р3а D5).
        notify.warning(draftChangedText(changed, changes?.title), { action: { label: "Обновить", onClick: () => void publishing.afterChange() } });
      } else {
        notify.error(pagePublishErrorText(err, "Страницу опубликовать не удалось — сайт остался прежним, черновик сохранён."));
      }
      // Черновик изменили — перечитать страницу, сводку и канву сразу; иначе — только сводку.
      void (changed ? publishing.afterChange() : publishing.reload());
    } finally {
      setBusy(false);
    }
  }

  function run() {
    if (action.kind === "page-now") void publishPage();
    else if (action.kind === "dialog") view.openDialog("publish");
  }

  return { action, busy, run };
}
